// 관리자 환경설정 (페이지별 마지막 방문 시각 등) — 가벼운 단일 도큐먼트
// adminPrefs/{uid}
//   lastVisitedAt: { [pageKey]: Timestamp }

import "firebase/compat/firestore";
import firebase from "firebase/compat/app";

const COLLECTION = "adminPrefs";

// 사이드바 NEW 뱃지 추적 대상 — key, 컬렉션명, 시간 필드명, (옵션) wheres: [[field, op, value], ...]
const TRACKED_PAGES = [
	{ key: 'users', collection: 'users', timeField: 'createAt' },
	// 프로필 관리 탭별 추적 (id prefix: profiles_) — 부모 'profiles' 뱃지는 NavItem 에서 자식 OR 집계
	{ key: 'profiles_technicians', collection: 'technicians', timeField: 'createAt' },
	{ key: 'profiles_constructionCompanys', collection: 'constructionCompanys', timeField: 'createAt' },
	{ key: 'profiles_materialSuppliers', collection: 'materialSuppliers', timeField: 'createAt' },
	{ key: 'profiles_toolComapnys', collection: 'toolComapnys', timeField: 'createAt' },
	{ key: 'profiles_manufacturings', collection: 'manufacturings', timeField: 'createAt' },
	{ key: 'profiles_interiors', collection: 'interiors', timeField: 'createAt' },
	{ key: 'profiles_panelCompanys', collection: 'panelCompanys', timeField: 'createAt' },
	{ key: 'profiles_hvacCompanys', collection: 'hvacCompanys', timeField: 'createAt' },
	{ key: 'profiles_institutions', collection: 'institutions', timeField: 'createAt' },
	{ key: 'profiles_etcCompanys', collection: 'etcCompanys', timeField: 'createAt' },
	{ key: 'profiles_duplicateCompanyInfos', collection: 'duplicateCompanyInfos', timeField: 'createAt' },
	// 구인 구직 탭별 추적 (id prefix: findTechnicians_) — 부모 'findTechnicians' 뱃지는 NavItem 에서 자식 OR 집계
	{ key: 'findTechnicians_hire_daily',     collection: 'findTechnicians', timeField: 'createAt', wheres: [['postType','==','PostType.hire'], ['jobType','==','JobType.daily']] },
	{ key: 'findTechnicians_hire_contract', collection: 'findTechnicians', timeField: 'createAt', wheres: [['postType','==','PostType.hire'], ['jobType','==','JobType.contract']] },
	{ key: 'findTechnicians_hire_fullTime', collection: 'findTechnicians', timeField: 'createAt', wheres: [['postType','==','PostType.hire'], ['jobType','==','JobType.fullTime']] },
	{ key: 'findTechnicians_seek_daily',     collection: 'findTechnicians', timeField: 'createAt', wheres: [['postType','==','PostType.seek'], ['jobType','==','JobType.daily']] },
	{ key: 'findTechnicians_seek_contract', collection: 'findTechnicians', timeField: 'createAt', wheres: [['postType','==','PostType.seek'], ['jobType','==','JobType.contract']] },
	{ key: 'findTechnicians_seek_fullTime', collection: 'findTechnicians', timeField: 'createAt', wheres: [['postType','==','PostType.seek'], ['jobType','==','JobType.fullTime']] },
	{ key: 'requestWorks', collection: 'requestWorks', timeField: 'createAt' },
	{ key: 'markets', collection: 'markets', timeField: 'createAt' },
	{ key: 'companyNewss', collection: 'companyNewss', timeField: 'createAt' },
	{ key: 'constructionDiarys', collection: 'constructionDiarys', timeField: 'createAt' },
	{ key: 'qnas', collection: 'qnas', timeField: 'createAt' },
	{ key: 'reports', collection: 'reports', timeField: 'createAt' },
	{ key: 'calcErrorReports', collection: 'calcErrorReports', timeField: 'createAt' },
	{ key: 'payments', collection: 'payments', timeField: 'createAt' },
	{ key: 'subscriptions', collection: 'subscriptions', timeField: 'createAt' },
];

class AdminPrefsDb {
	static collectionRef = () => firebase.firestore().collection(COLLECTION);
	static docRef = (uid) => firebase.firestore().collection(COLLECTION).doc(uid);
	static FieldValue = firebase.firestore.FieldValue;
	static serverTimestamp = () => firebase.firestore.FieldValue.serverTimestamp();

	// 도큐먼트 존재 보장 — 없으면 모든 추적 페이지를 현재 시각으로 시드(기존 데이터를 NEW 로 잘못 표시하지 않도록)
	// 기존 도큐먼트에 새로 추가된 추적 키가 있으면 누락분만 backfill
	static ensureExists = async (uid) => {
		if (!uid) return;
		const ref = AdminPrefsDb.docRef(uid);
		const snap = await ref.get();
		const now = firebase.firestore.Timestamp.now();
		if (!snap.exists) {
			const seed = {};
			TRACKED_PAGES.forEach(({ key }) => { seed[key] = now; });
			await ref.set({ lastVisitedAt: seed }, { merge: true });
			return;
		}
		const existing = snap.data()?.lastVisitedAt || {};
		const missing = {};
		TRACKED_PAGES.forEach(({ key }) => {
			if (!existing[key]) missing[key] = now;
		});
		if (Object.keys(missing).length > 0) {
			await ref.set({ lastVisitedAt: missing }, { merge: true });
		}
	}

	static load = async (uid) => {
		if (!uid) return null;
		const snap = await AdminPrefsDb.docRef(uid).get();
		return snap.exists ? snap.data() : null;
	}

	// 특정 페이지 키의 마지막 방문 시각을 serverTimestamp 으로 갱신
	static updateLastVisited = async (uid, pageKey) => {
		if (!uid || !pageKey) return;
		await AdminPrefsDb.docRef(uid).set({
			lastVisitedAt: {
				[pageKey]: AdminPrefsDb.serverTimestamp(),
			},
		}, { merge: true });
	}

	// 각 추적 페이지에 대해 "마지막 방문 이후 새 데이터 존재" 여부 1건씩 체크
	// 반환: { [pageKey]: boolean }
	static fetchNewBadges = async (uid) => {
		const result = {};
		if (!uid) return result;
		// race 방지: 시드 도큐먼트가 반드시 존재한 뒤 비교
		await AdminPrefsDb.ensureExists(uid);
		const prefs = await AdminPrefsDb.load(uid);
		const lastVisitedAt = prefs?.lastVisitedAt || {};
		const db = firebase.firestore();
		await Promise.all(TRACKED_PAGES.map(async ({ key, collection, timeField, wheres }) => {
			try {
				const last = lastVisitedAt[key];
				// 시드가 빠진 키(예: 새로 추가된 페이지)는 NEW 로 보지 않음 — 다음 방문 때 시드됨
				if (!last) {
					result[key] = false;
					return;
				}
				let q = db.collection(collection);
				if (Array.isArray(wheres)) {
					for (const [f, op, v] of wheres) q = q.where(f, op, v);
				}
				q = q.orderBy(timeField, 'desc').limit(1);
				const snap = await q.get();
				if (snap.empty) {
					result[key] = false;
					return;
				}
				const latest = snap.docs[0].data()[timeField];
				if (!latest) {
					result[key] = false;
					return;
				}
				result[key] = latest.toMillis() > last.toMillis();
			} catch (e) {
				console.warn(`[AdminPrefs] fetchNewBadges ${key} failed:`, e);
				result[key] = false;
			}
		}));
		return result;
	}
}

export { AdminPrefsDb, TRACKED_PAGES };
